import { useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { ArrowLongRightIcon } from '../components/icons/Icons.jsx';

const INPUT_CLASS =
  'w-full rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2.5 text-cream placeholder:text-ink-muted focus:border-gold/50';

export default function Auth() {
  const { t } = useTranslation();
  const { isSignedIn, loading, signInWithEmail } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Visitors bounced here by ProtectedRoute almost always have an account
  // already, so they land on the sign-in side; everyone else (nav "Register",
  // homepage CTA) starts on the registration side.
  const [mode, setMode] = useState(() =>
    searchParams.get('mode') === 'signin' || location.state?.from ? 'signin' : 'register',
  );
  const [email, setEmail] = useState('');
  const [researchGoals, setResearchGoals] = useState('');
  const [affiliation, setAffiliation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  if (!loading && isSignedIn) {
    const target = location.state?.from?.pathname ?? '/profile';
    return <Navigate to={target} replace />;
  }

  const isRegister = mode === 'register';
  const canSubmit = email.trim() && (!isRegister || researchGoals.trim());

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(
        email.trim(),
        isRegister
          ? { research_goals: researchGoals.trim(), affiliation: affiliation.trim() || null }
          : null,
      );
      setSent(true);
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-8 pb-20 pt-32">
      <div className="cta-box">
        <div className="section-label">{t('auth.label')}</div>
        <h1 className="cta-title">{t('auth.title')}</h1>
        <p className="cta-body">{t(isRegister ? 'auth.description' : 'auth.descriptionSignIn')}</p>

        {sent ? (
          <div className="rounded-md border border-gold/30 bg-navy-mid/30 p-6 text-left">
            <h2 className="font-serif text-xl text-gold-light">{t('auth.sent.title')}</h2>
            <p className="mt-2 text-sm text-ink-secondary">
              {t('auth.sent.body', { email })}
            </p>
            <button
              type="button"
              className="mt-4 text-sm font-medium text-gold hover:text-gold-light"
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
            >
              {t('auth.sent.useDifferentEmail')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div
              role="radiogroup"
              aria-label={t('auth.mode.label')}
              className="grid grid-cols-2 gap-1 rounded-md border border-white/10 bg-navy-deep/40 p-1"
            >
              <ModeButton selected={isRegister} onClick={() => setMode('register')}>
                {t('auth.mode.register')}
              </ModeButton>
              <ModeButton selected={!isRegister} onClick={() => setMode('signin')}>
                {t('auth.mode.signin')}
              </ModeButton>
            </div>

            <Field label={t('auth.emailLabel')}>
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>

            {isRegister && (
              <>
                <Field label={t('auth.researchGoalsLabel')} hint={t('auth.researchGoalsHint')}>
                  <textarea
                    required
                    rows={4}
                    maxLength={1000}
                    placeholder={t('auth.researchGoalsPlaceholder')}
                    value={researchGoals}
                    onChange={(e) => setResearchGoals(e.target.value)}
                    className={`${INPUT_CLASS} resize-y`}
                  />
                </Field>

                <Field label={t('auth.affiliationLabel')}>
                  <input
                    type="text"
                    autoComplete="organization"
                    maxLength={200}
                    placeholder={t('auth.affiliationPlaceholder')}
                    value={affiliation}
                    onChange={(e) => setAffiliation(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </Field>
              </>
            )}

            {error && (
              <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-left text-sm text-red-200">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !canSubmit}
            >
              {submitting ? t('common.loading') : t('auth.send')}
              <ArrowLongRightIcon style={{ width: 18, height: 18 }} />
            </button>

            <p className="text-xs text-ink-muted">{t('auth.hint')}</p>
          </form>
        )}
      </div>
    </div>
  );
}

function ModeButton({ selected, onClick, children }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={`rounded px-3 py-2 text-sm font-medium leading-tight transition-colors ${
        selected
          ? 'bg-navy-mid text-gold-light'
          : 'text-ink-secondary hover:text-cream'
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block text-left">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}
